import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Plus,
  Image as ImageIcon,
  Type,
  Square,
  SquareStack,
  Brush,
  Layers,
  Folder,
  Sliders,
  Eye,
  EyeOff,
  PenTool,
  HelpCircle,
  GripVertical,
} from 'lucide-react';
import { LayerOverflowMenu } from './LayerOverflowMenu';
import type { AdjustmentKind } from './adjustments';
import { computeDropTarget } from '../lib/layer-drop';
import type { Layer, LayerType, BlendMode, LayerEffects, EditorTool } from '../types';
import type { PropertiesPanelPlacement } from './PropertiesPanel';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/** Blend modes grouped by intent (Photoshop convention). The values
 *  are Canvas2D `globalCompositeOperation` strings — passed straight
 *  through to Fabric. */
const BLEND_MODE_GROUPS: { label: string; modes: { value: BlendMode; label: string }[] }[] = [
  { label: 'Normal', modes: [{ value: 'source-over', label: 'Normal' }] },
  {
    label: 'Darken',
    modes: [
      { value: 'darken', label: 'Darken' },
      { value: 'multiply', label: 'Multiply' },
      { value: 'color-burn', label: 'Color Burn' },
    ],
  },
  {
    label: 'Lighten',
    modes: [
      { value: 'lighten', label: 'Lighten' },
      { value: 'screen', label: 'Screen' },
      { value: 'color-dodge', label: 'Color Dodge' },
    ],
  },
  {
    label: 'Contrast',
    modes: [
      { value: 'overlay', label: 'Overlay' },
      { value: 'soft-light', label: 'Soft Light' },
      { value: 'hard-light', label: 'Hard Light' },
    ],
  },
  {
    label: 'Inversion',
    modes: [
      { value: 'difference', label: 'Difference' },
      { value: 'exclusion', label: 'Exclusion' },
    ],
  },
  {
    label: 'Component',
    modes: [
      { value: 'hue', label: 'Hue' },
      { value: 'saturation', label: 'Saturation' },
      { value: 'color', label: 'Color' },
      { value: 'luminosity', label: 'Luminosity' },
    ],
  },
];

const BLEND_MODE_LABEL: Record<BlendMode, string> = (() => {
  const out: Partial<Record<BlendMode, string>> = {};
  for (const g of BLEND_MODE_GROUPS) for (const m of g.modes) out[m.value] = m.label;
  return out as Record<BlendMode, string>;
})();

interface LayersPanelProps {
  isOpen: boolean;
  placement: PropertiesPanelPlacement;
  layers: Layer[];
  /** Depth in the layer tree for each id (0 = root, 1 = inside a
   *  top-level group, etc.). Drives the row's left indentation —
   *  Tier 0 documents have depth 0 everywhere; Tier 1 group
   *  nesting cascades visually here. */
  layerDepths: Record<string, number>;
  /** Set of group ids whose children are currently hidden in the
   *  panel (collapsed). Local UI state; not persisted. */
  collapsedGroupIds: Set<string>;
  onToggleCollapse: (id: string) => void;
  /** Group/Ungroup actions surfaced in the row's overflow menu
   *  and via the "+ Group" header button. Group wraps the active
   *  layer in a new group; ungroup is offered only on group rows. */
  onGroupActiveLayer: () => void;
  onUngroup: (id: string) => void;
  /** Open Free Transform on the given layer (Tier 1 Group E
   *  Phase 1). Surfaced in the row overflow menu. */
  onTransformLayer: (id: string) => void;
  /** Open the Adjust / Filter modal for the given layer with the
   *  chosen kind. Surfaced as the Adjust / Filter submenus in the
   *  row overflow menu (moved there from the Move tool's
   *  properties — these are per-layer actions). */
  onOpenAdjustment: (id: string, kind: AdjustmentKind) => void;
  onOpenFilter: (id: string, kind: AdjustmentKind) => void;
  /** Whether the given layer has rasterizable pixels — drives the
   *  Adjust / Filter submenus' visibility per row. */
  canAdjustLayer: (id: string) => boolean;
  /** Open the Liquify workspace on the given layer (Tier 1 Group
   *  E final piece). Surfaced in the row overflow menu next to
   *  Free Transform. */
  onLiquifyLayer: (id: string) => void;
  /** Open Path Edit mode on the given vector layer (Tier 2 Group
   *  A PR-B). The overflow item only surfaces on vector rows; the
   *  callback is plumbed unconditionally here. */
  onEditPathLayer: (id: string) => void;
  /** Crop the layer's backing pixels to the alpha bounding box of
   *  its non-transparent content. Surfaced in the row overflow
   *  menu next to Liquify. Eligibility (raster-backed, non-group,
   *  non-adjustment) is decided per-row inside the panel. */
  onTrimLayer: (id: string) => void;
  /** Move a single layer up or down by one slot in z-order. Drag-
   *  and-drop reorder lives at the panel level and uses
   *  `onMoveNodeInTree` — these two are exclusively for the
   *  overflow-menu Move Up / Move Down entries, which fan out to
   *  the full multi-selection at the EditorPage layer. */
  onMoveLayerUp: (id: string) => void;
  onMoveLayerDown: (id: string) => void;
  /** Merge the active layer + every additionally-selected layer
   *  into a single raster layer. Surfaced in the overflow menu
   *  only when the row participates in a 2+-layer same-parent
   *  selection. The handler is identity-stable (acts on the
   *  current selection, not on the row's id) — every row that
   *  qualifies invokes the same merge. */
  onMergeLayers: () => void;
  /** Tree-aware reorder via drag-and-drop: id moves to be a child
   *  of `parentId` (or root if null) at `index`. Replaces the flat
   *  reorder for tree-shaped documents — flat reorder is still
   *  used internally as a fast path for the common Tier 0 case
   *  via the `onReorderLayer` prop. */
  onMoveNodeInTree: (id: string, parentId: string | null, index: number) => void;
  activeLayerId: string | null;
  /** Whether the editor currently has a finalized marquee
   *  selection. Drives the "Add Mask From Selection" affordance —
   *  the seeded mask is dramatically more useful than a reveal-all
   *  default, but only available when there's something to seed
   *  from. */
  hasSelection: boolean;
  /** Per-layer edit target lookup — `editTargets[layerId]`
   *  determines whether brush strokes target the layer's pixels
   *  or its mask alpha. Default 'layer'. */
  editTargets: Record<string, 'layer' | 'mask'>;
  onSetEditTarget: (id: string, target: 'layer' | 'mask') => void;
  /** Set of additionally-selected layer ids beyond the active
   *  one. Shift+click extends a range from the active layer to
   *  the clicked row. Empty when nothing extra is selected.
   *  Group from header / overflow uses (activeLayerId + this set)
   *  as the multi-select. */
  selectedLayerIds: Set<string>;
  onShiftSelectLayer: (id: string) => void;
  /** Multi-select mode (Tier 2 Group A PR-C). When ON, taps on
   *  layer rows toggle membership in the additional-selection set
   *  rather than activating a different layer — mobile-friendly
   *  alternative to Ctrl/Cmd+click. The active layer doesn't
   *  change while in this mode. */
  multiSelectMode: boolean;
  onToggleMultiSelectMode: () => void;
  /** Toggle a layer id in/out of the additional-selection set.
   *  Drives the Ctrl/Cmd+click and Multi-mode tap paths. The
   *  active layer is never togglable through this. */
  onToggleSelectLayer: (id: string) => void;
  onAddLayer: () => void;
  /** Icon-area click handler. Always selects the layer in a way that
   *  surfaces its bounding box / transform handles (modulo tool
   *  lock). The icon's onClick also auto-switches the editor tool
   *  for text/shape/vector layers, then re-calls this so the bbox
   *  shows in the just-switched tool. */
  onSelectLayer: (id: string) => void;
  /** Row-area click handler — equivalent of clicking the layer's
   *  name. Sets the active layer (panel highlight + brush target)
   *  but, in non-Move tools, EditorPage discards Fabric's active
   *  object so the transform handles don't render. The icon path
   *  intentionally bypasses this; only the row's body uses it. */
  onSelectLayerFromName: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onRenameLayer: (id: string, name: string) => void;
  onDeleteLayer: (id: string) => void;
  onSetLayerOpacity: (id: string, opacity: number) => void;
  onSetLayerBlendMode: (id: string, blendMode: BlendMode) => void;
  onSetLayerEffects: (id: string, effects: LayerEffects) => void;
  /** Add a raster mask to the given layer. If a marquee is active
   *  the panel passes `fromMarquee: true` so the EditorPage seeds
   *  the mask alpha from the selection. */
  onAddMaskToLayer: (id: string, opts?: { fromMarquee?: boolean }) => void;
  onDeleteMaskFromLayer: (id: string) => void;
  /** Toggle the `enabled` flag on a layer's existing mask. No-op
   *  when the layer has no mask. */
  onToggleMaskEnabled: (id: string) => void;
  /** Toggle the `inverted` flag on a layer's existing mask. No-op
   *  when the layer has no mask. */
  onToggleMaskInverted: (id: string) => void;
  /** Add a vector mask to the given layer. Tier 2 Group A PR-D.
   *  EditorPage creates a raster mask first (same Fabric pipeline)
   *  then immediately converts to vector kind and seeds the
   *  vectorPath with a canvas-rect (reveal-all). */
  onAddVectorMaskToLayer: (id: string) => void;
  /** Convert the existing mask between raster and vector kinds.
   *  Tier 2 Group A PR-D. EditorPage owns the trace / seed logic
   *  + the post-convert raster pass. */
  onConvertMaskToVector: (id: string) => void;
  onConvertMaskToRaster: (id: string) => void;
  /** Enter Path Edit mode on a vector mask's path. No-op when the
   *  layer's mask isn't a vector kind. Tier 2 Group A PR-D. */
  onEditVectorMaskPath: (id: string) => void;
  /** Tap an adjustment-layer thumb → open the Adjustments modal
   *  in non-destructive edit mode pre-filled with that layer's
   *  current config. EditorPage hands the modal the layerId so
   *  every value change dispatches `UPDATE_ADJUSTMENT` against
   *  the right node. */
  onEditAdjustment: (id: string) => void;
  /** Switch the editor's active tool. Used by the layer-thumb click
   *  to auto-select the matching tool (text/shape/pen) when the
   *  user activates a text, shape, or vector layer — surfaces the
   *  right properties panel without a separate toolbar click. */
  onSetActiveTool: (tool: EditorTool) => void;
  onToggle: () => void;
  /** Open the help drawer at this panel's documentation. */
  onOpenHelp: () => void;
}

const TYPE_ICONS: Record<LayerType, React.ComponentType<{ className?: string }>> = {
  image: ImageIcon,
  text: Type,
  shape: Square,
  paint: Brush,
  empty: Layers,
  // Group + adjustment are reserved for upcoming Tier 1 chunks; the
  // icons get used as soon as the LayersPanel rewrite surfaces
  // group/adjustment rows. Mapped here so the type system enforces
  // exhaustiveness and so any pre-rewrite seed data renders sanely.
  group: Folder,
  adjustment: Sliders,
  vector: PenTool,
};

export function LayersPanel({
  isOpen,
  placement,
  layers,
  activeLayerId,
  onAddLayer,
  onSelectLayer,
  onSelectLayerFromName,
  onToggleVisibility,
  onRenameLayer,
  onDeleteLayer,
  onSetLayerOpacity,
  onSetLayerBlendMode,
  onSetLayerEffects,
  onAddMaskToLayer,
  onDeleteMaskFromLayer,
  onToggleMaskEnabled,
  onToggleMaskInverted,
  onAddVectorMaskToLayer,
  onConvertMaskToVector,
  onConvertMaskToRaster,
  onEditVectorMaskPath,
  onEditAdjustment,
  onSetActiveTool,
  hasSelection,
  editTargets,
  onSetEditTarget,
  selectedLayerIds,
  onShiftSelectLayer,
  multiSelectMode,
  onToggleMultiSelectMode,
  onToggleSelectLayer,
  layerDepths,
  collapsedGroupIds,
  onToggleCollapse,
  onGroupActiveLayer,
  onUngroup,
  onTransformLayer,
  onOpenAdjustment,
  onOpenFilter,
  canAdjustLayer,
  onLiquifyLayer,
  onEditPathLayer,
  onTrimLayer,
  onMergeLayers,
  onMoveLayerUp,
  onMoveLayerDown,
  onMoveNodeInTree,
  onToggle,
  onOpenHelp,
}: LayersPanelProps) {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  // Drop target as a (rowIndex, edge) pair — `edge` is which side
  // of the row the pointer is hovering, drives the insertion line.
  const [dragOver, setDragOver] = useState<{ index: number; edge: 'top' | 'bottom' } | null>(null);
  const isRight = placement === 'right';

  // Ref shadows of drag-and-tree state, kept synced via the
  // setter wrappers below. Stable drag callbacks (which need
  // `[]` deps so React.memo on LayerRow holds) read these
  // instead of closing over the React state.
  const draggingIndexRef = useRef<number | null>(null);
  const layersRef = useRef(layers);
  layersRef.current = layers;
  const collapsedGroupIdsRef = useRef(collapsedGroupIds);
  collapsedGroupIdsRef.current = collapsedGroupIds;

  // shrink-0 + content height: the layer list extends to its full height
  // and never compresses its siblings. The rail (EditorPage) owns the
  // single scrollbar across Properties / History / Layers — open panels
  // extend down rather than each carrying their own inner scrollbar.
  const containerClass = isRight
    ? 'w-full bg-background border-l border-t border-border flex flex-col shrink-0'
    : 'w-full bg-background border-t border-border flex flex-col shrink-0';

  const HideIcon = ChevronDown;

  // Filter rows by collapse state. A row is rendered only when
  // every group ancestor is expanded. Walks top-to-bottom once
  // and collects descendants of any collapsed group.
  const hiddenByCollapse = useMemo(() => {
    const hidden = new Set<string>();
    let blockingDepth: number | null = null;
    for (const layer of layers) {
      const depth = layerDepths[layer.id] ?? 0;
      if (blockingDepth !== null && depth > blockingDepth) {
        hidden.add(layer.id);
        continue;
      }
      blockingDepth = null;
      if (layer.type === 'group' && collapsedGroupIds.has(layer.id)) {
        blockingDepth = depth;
      }
    }
    return hidden;
  }, [layers, layerDepths, collapsedGroupIds]);

  const visibleLayers = useMemo(
    () => layers.filter((l) => !hiddenByCollapse.has(l.id)),
    [layers, hiddenByCollapse],
  );

  // Set of layer ids eligible to surface the "Merge Layers" menu
  // entry. Eligibility requires:
  //   - the row is part of the active + multi-select union
  //   - that union has ≥2 same-parent members (anything less can't
  //     be "merged" — there's nothing to merge with)
  // Computed once per relevant-state change so memoized LayerRow
  // instances re-render only when their own eligibility flips.
  const mergeEligibleIds = useMemo(() => {
    if (!activeLayerId) return new Set<string>();
    const layerById = new Map(layers.map((l) => [l.id, l] as const));
    const active = layerById.get(activeLayerId);
    if (!active) return new Set<string>();
    const targetParent = active.parentId ?? null;
    const ids = [activeLayerId, ...Array.from(selectedLayerIds)].filter((id) => {
      const node = layerById.get(id);
      return node !== undefined && (node.parentId ?? null) === targetParent;
    });
    if (ids.length < 2) return new Set<string>();
    return new Set(ids);
  }, [activeLayerId, selectedLayerIds, layers]);

  // id → flat-list index. Used by per-row dragInsertionEdge,
  // canMoveUp/Down, and the drop handler. Replaces an O(N²)
  // `layers.findIndex(l => l.id === layer.id)` inside `.map`.
  const idToIndex = useMemo(() => {
    const m = new Map<string, number>();
    for (let i = 0; i < layers.length; i++) m.set(layers[i].id, i);
    return m;
  }, [layers]);

  // Stable setter wrapper: keeps `draggingIndexRef` in sync so
  // the drag callbacks below can read the live value via ref
  // without taking it as a useCallback dep.
  const setDraggingIndexBoth = useCallback((n: number | null) => {
    draggingIndexRef.current = n;
    setDraggingIndex(n);
  }, []);

  // Stable drag callbacks. All have `[]` deps so React.memo on
  // LayerRow doesn't bust on every parent render. Reads of
  // current state happen via the *Ref shadows above.
  const handleDragStart = useCallback(
    (index: number) => {
      setDraggingIndexBoth(index);
    },
    [setDraggingIndexBoth],
  );

  const handleDragEnter = useCallback((index: number) => {
    if (draggingIndexRef.current === null) return;
    setDragOver({ index, edge: 'top' });
  }, []);

  const handleDragOver = useCallback((index: number, edge: 'top' | 'bottom') => {
    const di = draggingIndexRef.current;
    if (di === null || di === index) return;
    setDragOver((cur) => (cur?.index === index && cur.edge === edge ? cur : { index, edge }));
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingIndexBoth(null);
    setDragOver(null);
  }, [setDraggingIndexBoth]);

  const handleDrop = useCallback(
    (index: number, edge: 'top' | 'bottom') => {
      const di = draggingIndexRef.current;
      const currentLayers = layersRef.current;
      const currentCollapsed = collapsedGroupIdsRef.current;
      if (di !== null && di !== index) {
        const sourceLayer = currentLayers[di];
        const targetLayer = currentLayers[index];
        if (sourceLayer && targetLayer) {
          const drop = computeDropTarget(
            currentLayers,
            sourceLayer,
            targetLayer,
            edge,
            currentCollapsed,
          );
          onMoveNodeInTree(sourceLayer.id, drop.parentId, drop.index);
        }
      }
      setDraggingIndexBoth(null);
      setDragOver(null);
    },
    [onMoveNodeInTree, setDraggingIndexBoth],
  );

  // Collapsed state — renders just a "Show layers" affordance.
  // Placed AFTER every hook in this component so the hook count
  // stays constant across `isOpen` flips; an early return above
  // the `useMemo` / `useCallback` block triggered React's
  // rule-of-hooks invariant ("Rendered fewer hooks than expected")
  // when the user clicked Hide.
  if (!isOpen) {
    if (isRight) {
      return (
        <button
          type="button"
          className="h-10 w-full bg-muted border-l border-t border-border flex items-center justify-between px-3 text-muted-foreground hover:text-foreground shrink-0"
          onClick={onToggle}
          title="Show layers"
        >
          <span className="text-xs font-medium">Layers</span>
          <ChevronUp className="h-4 w-4" />
        </button>
      );
    }
    return (
      <button
        type="button"
        className="w-full h-10 bg-muted border-t border-border flex items-center justify-between px-3 text-muted-foreground hover:text-foreground shrink-0"
        onClick={onToggle}
        title="Show layers"
      >
        <span className="text-xs font-medium">Layers</span>
        <ChevronUp className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className={containerClass}>
      <div className="flex items-center justify-between px-3 h-10 bg-muted border-b border-border shrink-0">
        <h3 className="text-sm font-medium">Layers</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
            onClick={onOpenHelp}
            title="Help for the Layers panel"
            aria-label="Open help for the Layers panel"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
            onClick={onAddLayer}
            title="Add new layer"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={`p-1 rounded ${
              multiSelectMode
                ? 'bg-destructive/10 text-destructive ring-1 ring-destructive'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
            onClick={onToggleMultiSelectMode}
            title={
              multiSelectMode
                ? 'Multi-select on — taps toggle layers in selection'
                : 'Multi-select off — tap layer rows to add them to the selection'
            }
            aria-label="Toggle multi-select mode"
            aria-pressed={multiSelectMode}
          >
            <SquareStack className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-30 disabled:pointer-events-none"
            onClick={onGroupActiveLayer}
            disabled={!activeLayerId}
            title="Group active layer"
            aria-label="Group active layer"
          >
            <Folder className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 ml-1"
            onClick={onToggle}
          >
            Hide
            <HideIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* No internal scroll: Layers is the last panel in the rail, so you
          scroll the rail to reach the bottom of the list anyway — a second
          inner scrollbar would just be redundant. The list extends to full
          height and the rail (EditorPage) scrolls as one. min-h-[44px] keeps
          one row's height when empty so the "No layers yet" hint isn't
          cramped. */}
      <div className="min-h-[44px]">
        {layers.length === 0 ? (
          <div className="p-3 text-xs text-muted-foreground italic">No layers yet.</div>
        ) : (
          <ul>
            {visibleLayers.map((layer) => {
              // Drag indices reference the FULL flat list — that's
              // what `reorderFlattened` operates on. The id→index
              // map gives O(1) lookup; previously this was an O(N)
              // findIndex inside .map → O(N²) per render.
              const index = idToIndex.get(layer.id) ?? -1;
              const depth = layerDepths[layer.id] ?? 0;
              const isGroup = layer.type === 'group';
              const isCollapsed = isGroup && collapsedGroupIds.has(layer.id);
              return (
                <LayerRow
                  key={layer.id}
                  layer={layer}
                  index={index}
                  depth={depth}
                  isGroup={isGroup}
                  isCollapsed={isCollapsed}
                  isActive={layer.id === activeLayerId}
                  isSelected={selectedLayerIds.has(layer.id)}
                  dragInsertionEdge={
                    dragOver?.index === index && draggingIndex !== null && draggingIndex !== index
                      ? dragOver.edge
                      : null
                  }
                  canMoveUp={index > 0}
                  canMoveDown={index < layers.length - 1}
                  hasMask={!!layer.mask}
                  maskKind={layer.mask?.kind ?? 'raster'}
                  maskEnabled={layer.mask?.enabled ?? false}
                  maskInverted={layer.mask?.inverted ?? false}
                  hasSelection={hasSelection}
                  editTarget={editTargets[layer.id] ?? 'layer'}
                  onToggleCollapse={onToggleCollapse}
                  onUngroup={onUngroup}
                  onTransform={onTransformLayer}
                  onOpenAdjustment={onOpenAdjustment}
                  onOpenFilter={onOpenFilter}
                  canAdjustLayer={canAdjustLayer}
                  onLiquify={onLiquifyLayer}
                  onEditPath={onEditPathLayer}
                  onTrim={onTrimLayer}
                  onMerge={onMergeLayers}
                  canMerge={mergeEligibleIds.has(layer.id)}
                  // isMultiSelected uses the same predicate — the
                  // row is in a 2+-layer same-parent selection. The
                  // overflow menu uses it to disable inapplicable
                  // entries (Rename, Free Transform, Liquify, mask
                  // sub-ops); they'd otherwise misleadingly act on
                  // only this row.
                  isMultiSelected={mergeEligibleIds.has(layer.id)}
                  onShiftSelect={onShiftSelectLayer}
                  onToggleSelect={onToggleSelectLayer}
                  multiSelectMode={multiSelectMode}
                  onSetEditTarget={onSetEditTarget}
                  onSelect={onSelectLayer}
                  onSelectFromName={onSelectLayerFromName}
                  onToggleVisibility={onToggleVisibility}
                  onRename={onRenameLayer}
                  onDelete={onDeleteLayer}
                  onMoveLayerUp={onMoveLayerUp}
                  onMoveLayerDown={onMoveLayerDown}
                  onAddMask={onAddMaskToLayer}
                  onDeleteMask={onDeleteMaskFromLayer}
                  onToggleMaskEnabled={onToggleMaskEnabled}
                  onToggleMaskInverted={onToggleMaskInverted}
                  onAddVectorMask={onAddVectorMaskToLayer}
                  onConvertMaskToVector={onConvertMaskToVector}
                  onConvertMaskToRaster={onConvertMaskToRaster}
                  onEditVectorMaskPath={onEditVectorMaskPath}
                  onEditAdjustment={onEditAdjustment}
                  onSetActiveTool={onSetActiveTool}
                  onDragStart={handleDragStart}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDrop={handleDrop}
                />
              );
            })}
          </ul>
        )}
      </div>

      <SelectedLayerFooter
        layer={activeLayerId ? (layers.find((l) => l.id === activeLayerId) ?? null) : null}
        isBottommostVisible={activeLayerId ? isBottommostVisibleLeaf(layers, activeLayerId) : false}
        onSetLayerOpacity={onSetLayerOpacity}
        onSetLayerBlendMode={onSetLayerBlendMode}
        onSetLayerEffects={onSetLayerEffects}
      />
    </div>
  );
}

/** True when `layerId` is the lowest-rendered VISIBLE non-group leaf
 *  in the panel — i.e. there's nothing visible underneath it for a
 *  blend mode to composite against. The panel renders top-to-bottom
 *  (index 0 = top of stack, last index = bottom of stack), so we
 *  walk from the end skipping invisible rows and group containers
 *  (groups have no Fabric object of their own). When the first
 *  match is the target layer, it has nothing visible below it. */
function isBottommostVisibleLeaf(layers: Layer[], layerId: string): boolean {
  for (let i = layers.length - 1; i >= 0; i--) {
    const l = layers[i];
    if (l.type === 'group') continue;
    if (!l.visible) continue;
    return l.id === layerId;
  }
  return false;
}

/** Default values used when the user enables an effect with no
 *  prior config. Tuned to "subtle but visible" so the effect shows
 *  up immediately without the user having to fiddle with sliders. */
const DEFAULT_DROP_SHADOW = { color: '#00000080', blur: 8, offsetX: 4, offsetY: 4 };
const DEFAULT_STROKE = { color: '#000000', width: 2 };
const DEFAULT_GLOW = { color: '#ffe680', blur: 16, intensity: 1.4 };

/** Footer at the bottom of LayersPanel showing per-layer settings
 *  (opacity, blend mode, effects) for the selected layer. Hidden
 *  when no layer is selected so the panel doesn't reserve space
 *  when there's nothing to show. */
function SelectedLayerFooter({
  layer,
  isBottommostVisible,
  onSetLayerOpacity,
  onSetLayerBlendMode,
  onSetLayerEffects,
}: {
  layer: Layer | null;
  /** When true, the layer has nothing visible rendered beneath it,
   *  so any blend mode other than Normal would produce no visible
   *  effect (or worse — most modes simply make the layer disappear
   *  against a transparent canvas). Disables the blend dropdown
   *  with a tooltip so the user isn't left wondering why their
   *  mode change "doesn't do anything." */
  isBottommostVisible: boolean;
  onSetLayerOpacity: (id: string, opacity: number) => void;
  onSetLayerBlendMode: (id: string, blendMode: BlendMode) => void;
  onSetLayerEffects: (id: string, effects: LayerEffects) => void;
}) {
  const [effectsOpen, setEffectsOpen] = useState(false);
  if (!layer) return null;
  const opacity = layer.opacity ?? 1;
  const blendMode: BlendMode = layer.blendMode ?? 'source-over';
  const opacityPct = Math.round(opacity * 100);
  const effects = layer.effects ?? {};
  const dropShadow = effects.dropShadow;
  const stroke = effects.stroke;
  const glow = effects.glow;

  const updateEffects = (next: LayerEffects) => onSetLayerEffects(layer.id, next);

  return (
    <div className="border-t border-border px-3 py-2 space-y-2 shrink-0">
      <label className="flex items-center gap-2">
        <span className="text-xs w-14 shrink-0 text-muted-foreground">Opacity</span>
        <input
          type="range"
          min={0}
          max={100}
          value={opacityPct}
          onChange={(e) => onSetLayerOpacity(layer.id, Number(e.target.value) / 100)}
          className="flex-1"
          aria-label="Layer opacity"
        />
        <span className="text-xs w-10 text-right tabular-nums">{opacityPct}%</span>
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14 shrink-0 text-muted-foreground">Blend</span>
        <Select
          value={blendMode}
          onValueChange={(v) => onSetLayerBlendMode(layer.id, v as BlendMode)}
          disabled={isBottommostVisible}
        >
          <SelectTrigger
            className="flex-1 h-8 text-xs"
            aria-label="Layer blend mode"
            title={
              isBottommostVisible
                ? 'Blend modes need a layer below to composite against — add or unhide another layer beneath this one.'
                : undefined
            }
          >
            <SelectValue>{BLEND_MODE_LABEL[blendMode]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {BLEND_MODE_GROUPS.map((group, i) => (
              <SelectGroup key={group.label}>
                {i > 0 && <div className="h-px bg-border my-1" />}
                <SelectLabel className="text-[10px] uppercase text-muted-foreground">
                  {group.label}
                </SelectLabel>
                {group.modes.map((m) => (
                  <SelectItem key={m.value} value={m.value} className="text-xs">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </label>

      {/* Effects accordion. Collapsed by default to keep the footer
          tight; per-layer effects are an occasional tool, not a
          continuous control like opacity. */}
      <div className="border border-border rounded-md">
        <button
          type="button"
          onClick={() => setEffectsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground"
          aria-expanded={effectsOpen}
        >
          <span className="flex items-center gap-1.5">
            {effectsOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5 rotate-180" />
            )}
            Effects
          </span>
          {/* Active-effects pill so users can see at a glance whether
              any effect is enabled while the accordion is collapsed. */}
          {(dropShadow || stroke || glow) && (
            <span className="text-[10px] uppercase tracking-wider text-primary">
              {[dropShadow && 'Shadow', stroke && 'Stroke', glow && 'Glow']
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </button>
        {effectsOpen && (
          <div className="px-2 pb-2 space-y-2">
            <EffectBlock
              label="Drop Shadow"
              enabled={!!dropShadow}
              onToggle={(on) =>
                updateEffects({
                  ...effects,
                  dropShadow: on ? (dropShadow ?? DEFAULT_DROP_SHADOW) : undefined,
                })
              }
            >
              {dropShadow && (
                <>
                  <ColorAndSlider
                    label="Color"
                    color={dropShadow.color}
                    onColorChange={(c) =>
                      updateEffects({ ...effects, dropShadow: { ...dropShadow, color: c } })
                    }
                  />
                  <SliderRow
                    label="Blur"
                    value={dropShadow.blur}
                    min={0}
                    max={64}
                    onChange={(v) =>
                      updateEffects({ ...effects, dropShadow: { ...dropShadow, blur: v } })
                    }
                  />
                  <SliderRow
                    label="X"
                    value={dropShadow.offsetX}
                    min={-64}
                    max={64}
                    onChange={(v) =>
                      updateEffects({ ...effects, dropShadow: { ...dropShadow, offsetX: v } })
                    }
                  />
                  <SliderRow
                    label="Y"
                    value={dropShadow.offsetY}
                    min={-64}
                    max={64}
                    onChange={(v) =>
                      updateEffects({ ...effects, dropShadow: { ...dropShadow, offsetY: v } })
                    }
                  />
                </>
              )}
            </EffectBlock>
            <EffectBlock
              label="Stroke"
              enabled={!!stroke}
              onToggle={(on) =>
                updateEffects({
                  ...effects,
                  stroke: on ? (stroke ?? DEFAULT_STROKE) : undefined,
                })
              }
            >
              {stroke && (
                <>
                  <ColorAndSlider
                    label="Color"
                    color={stroke.color}
                    onColorChange={(c) =>
                      updateEffects({ ...effects, stroke: { ...stroke, color: c } })
                    }
                  />
                  <SliderRow
                    label="Width"
                    value={stroke.width}
                    min={0}
                    max={32}
                    onChange={(v) => updateEffects({ ...effects, stroke: { ...stroke, width: v } })}
                  />
                </>
              )}
            </EffectBlock>
            <EffectBlock
              label="Glow"
              enabled={!!glow}
              onToggle={(on) =>
                updateEffects({
                  ...effects,
                  glow: on ? (glow ?? DEFAULT_GLOW) : undefined,
                })
              }
            >
              {glow && (
                <>
                  <ColorAndSlider
                    label="Color"
                    color={glow.color}
                    onColorChange={(c) =>
                      updateEffects({ ...effects, glow: { ...glow, color: c } })
                    }
                  />
                  <SliderRow
                    label="Blur"
                    value={glow.blur}
                    min={0}
                    max={64}
                    onChange={(v) => updateEffects({ ...effects, glow: { ...glow, blur: v } })}
                  />
                  <SliderRow
                    label="Strength"
                    value={Math.round(glow.intensity * 100)}
                    min={10}
                    max={300}
                    onChange={(v) =>
                      updateEffects({ ...effects, glow: { ...glow, intensity: v / 100 } })
                    }
                  />
                </>
              )}
            </EffectBlock>
          </div>
        )}
      </div>
    </div>
  );
}

function EffectBlock({
  label,
  enabled,
  onToggle,
  children,
}: {
  label: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="cursor-pointer"
        />
        <span className="font-medium">{label}</span>
      </label>
      {enabled && <div className="pl-5 space-y-1.5">{children}</div>}
    </div>
  );
}

function SliderRow({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-[11px] w-10 shrink-0 text-muted-foreground">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1"
      />
      <span className="text-[11px] w-8 text-right tabular-nums">{value}</span>
    </label>
  );
}

function ColorAndSlider({
  label,
  color,
  onColorChange,
}: {
  label: string;
  color: string;
  onColorChange: (color: string) => void;
}) {
  // Native <input type=color> doesn't accept alpha; strip alpha for the
  // picker and preserve it on outbound update. Most users just pick a
  // base hue here; full alpha control comes in Tier 0 ColorSwatchPopover.
  const baseHex = color.length >= 7 ? color.substring(0, 7) : color;
  return (
    <label className="flex items-center gap-2">
      <span className="text-[11px] w-10 shrink-0 text-muted-foreground">{label}</span>
      <input
        type="color"
        value={baseHex}
        onChange={(e) => {
          // Re-attach the original alpha (last 2 hex chars) if present.
          const alpha = color.length === 9 ? color.substring(7) : '';
          onColorChange(e.target.value + alpha);
        }}
        className="w-7 h-7 rounded border border-border cursor-pointer"
      />
    </label>
  );
}

/** Long-press duration before mobile users enter rename mode.
 *  500ms balances "deliberate" (won't fire on a normal tap) against
 *  "responsive" (still feels like a quick gesture). */
const LONG_PRESS_MS = 500;

/** Touch / pen ("coarse") pointer device. Drives the touch-only
 *  drag-reorder grip handle: HTML5 drag-and-drop (the desktop row
 *  reorder) never fires on touch, so coarse-pointer devices get an
 *  explicit grip that reorders via pointer events instead. Evaluated
 *  once at module load — the input modality doesn't change mid-session
 *  in practice. */
const IS_COARSE_POINTER =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

interface LayerRowProps {
  layer: Layer;
  index: number;
  /** Depth in the layer tree — drives left indentation. 0 = root,
   *  1 = inside a top-level group, etc. Capped visually at 4
   *  levels (deeper rows render at the 4-level indent). */
  depth: number;
  isGroup: boolean;
  isCollapsed: boolean;
  isActive: boolean;
  /** True when this row is part of a multi-selection (shift-click
   *  range). Distinct from `isActive`: only one layer can be
   *  active at a time, but multiple can be selected. */
  isSelected: boolean;
  /** Which edge of THIS row should display the insertion line, or
   *  null when this row is not the current drop target. The edge
   *  is decided by the pointer's vertical position within the row
   *  (top half → 'top', bottom half → 'bottom'). */
  dragInsertionEdge: 'top' | 'bottom' | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
  hasMask: boolean;
  /** Mask kind — 'raster' (default) or 'vector'. Surfaced to the
   *  overflow menu as the Convert entry's target kind and as a
   *  guard on the mask-thumb tap (vector masks route into Path
   *  Edit instead of switching edit target). Tier 2 Group A PR-D. */
  maskKind: 'raster' | 'vector';
  /** Current `mask.enabled` value, surfaced to the overflow menu as
   *  the Disable / Enable toggle label. False is a valid state even
   *  when `hasMask` is true (a disabled mask still has painted alpha
   *  underneath; the toggle preserves it). */
  maskEnabled: boolean;
  /** Current `mask.inverted` value, surfaced to the overflow menu
   *  as the Invert / Un-invert toggle label. */
  maskInverted: boolean;
  hasSelection: boolean;
  /** Which surface receives brush/eraser strokes for this layer.
   *  'layer' (default) = the layer's pixel surface. 'mask' = the
   *  mask alpha. Mask-thumb-click sets this to 'mask'; layer-thumb-
   *  click sets it back to 'layer'. The thumb whose value matches
   *  gets the primary border. */
  editTarget: 'layer' | 'mask';
  // All callbacks are id-/index-taking and stable across the
  // parent's renders (built via useCallback / direct passthrough
  // of parent props). Internal closures bind the row's own id /
  // index where needed; React.memo on this component skips re-
  // render when no prop value has changed.
  onToggleCollapse: (id: string) => void;
  /** Surfaces the "Ungroup" overflow item only on group rows. */
  onUngroup: (id: string) => void;
  /** Surfaces the "Free Transform…" overflow item on non-group
   *  rows. Group transform is deferred (would need flatten-or-
   *  recurse). */
  onTransform: (id: string) => void;
  /** Open the Adjust / Filter modal for the given layer + kind.
   *  Surfaced as the Adjust / Filter submenus in the row overflow
   *  menu — per-layer actions moved off the Move tool's properties. */
  onOpenAdjustment: (id: string, kind: AdjustmentKind) => void;
  onOpenFilter: (id: string, kind: AdjustmentKind) => void;
  /** Whether the given layer has rasterizable pixels — drives the
   *  Adjust / Filter submenus' per-row visibility. */
  canAdjustLayer: (id: string) => boolean;
  /** Surfaces the "Liquify…" overflow item on non-group rows.
   *  Same group-exclusion reasoning as `onTransform`. */
  onLiquify: (id: string) => void;
  /** Surfaces the "Edit Path…" overflow item on vector rows.
   *  Reads `layer.type === 'vector'` inside LayerRow to decide
   *  visibility — no extra prop needed by the caller. */
  onEditPath: (id: string) => void;
  /** Surfaces the "Trim Transparent Pixels" overflow item on
   *  raster-backed rows (paint / image / paste). The row decides
   *  visibility based on `layer.type` — group / adjustment / text
   *  / shape / vector layers hide the entry. */
  onTrim: (id: string) => void;
  /** Surfaces the "Merge Layers" overflow item when `canMerge`
   *  is true. The handler ignores the row id (merge acts on the
   *  current activeLayerId + selectedLayerIds), so a single stable
   *  callback is passed to every row that qualifies. */
  onMerge: () => void;
  /** True when this row participates in a 2+-layer same-parent
   *  selection. Drives the Merge Layers entry's visibility. */
  canMerge: boolean;
  /** Same predicate as `canMerge`. Routed separately to the
   *  overflow menu so the gray-out logic can stay independent of
   *  the merge feature in case they diverge later (e.g. allowing
   *  merge of 1 layer to flatten effects). */
  isMultiSelected: boolean;
  /** Shift-click handler — extends the selection range from the
   *  active layer to this row. */
  onShiftSelect: (id: string) => void;
  /** Toggle this row in/out of the additional-selection set.
   *  Routed from Ctrl/Cmd+click and Multi-mode tap. */
  onToggleSelect: (id: string) => void;
  /** Multi-select mode flag — when true, plain taps on selection-
   *  participating areas (row body, name, thumbs) toggle this row
   *  in the selection instead of activating it. */
  multiSelectMode: boolean;
  onSetEditTarget: (id: string, target: 'layer' | 'mask') => void;
  /** Icon-area select — always tries to show the layer's bbox. The
   *  icon onClick re-calls this after auto-switching the tool
   *  (text/shape/vector) so the bbox shows in the new tool. */
  onSelect: (id: string) => void;
  /** Row-area (name) select — sets the active layer but in non-Move
   *  tools EditorPage clears Fabric's active object so the transform
   *  handles don't render. */
  onSelectFromName: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  /** Move-by-1 entries on the row overflow menu. Drag-and-drop
   *  reorder lives at the panel level (it needs DOM events, not
   *  per-row state), so the row receives only these two — they
   *  fan out to the whole multi-selection at the EditorPage
   *  layer. */
  onMoveLayerUp: (id: string) => void;
  onMoveLayerDown: (id: string) => void;
  onAddMask: (id: string, opts?: { fromMarquee?: boolean }) => void;
  onDeleteMask: (id: string) => void;
  onToggleMaskEnabled: (id: string) => void;
  onToggleMaskInverted: (id: string) => void;
  /** Add a vector mask seeded with a canvas-rect reveal-all path.
   *  Tier 2 Group A PR-D. */
  onAddVectorMask: (id: string) => void;
  /** Convert between raster and vector mask kinds. EditorPage
   *  owns the trace (raster→vector) / no-op (vector→raster) logic.
   *  Tier 2 Group A PR-D. */
  onConvertMaskToVector: (id: string) => void;
  onConvertMaskToRaster: (id: string) => void;
  /** Enter Path Edit mode on a vector mask's path. Surfaced as
   *  the mask-thumb tap when `maskKind === 'vector'` and as an
   *  overflow menu entry. Tier 2 Group A PR-D. */
  onEditVectorMaskPath: (id: string) => void;
  /** Tap the thumb of an adjustment layer to open the modal in
   *  non-destructive edit mode. No-op for non-adjustment rows. */
  onEditAdjustment: (id: string) => void;
  /** Switch the editor's active tool. Fired by the thumb click for
   *  text/shape/vector rows so the matching tool surfaces its
   *  properties without a separate toolbar click. */
  onSetActiveTool: (tool: EditorTool) => void;
  onDragStart: (index: number) => void;
  onDragEnter: (index: number) => void;
  onDragEnd: () => void;
  onDrop: (index: number, edge: 'top' | 'bottom') => void;
  onDragOver: (index: number, edge: 'top' | 'bottom') => void;
}

/** Exported for unit tests only — verifies icon-vs-name click
 *  routing. Not part of the public LayersPanel API. */
export const LayerRow = memo(function LayerRow({
  layer,
  index,
  depth,
  isGroup,
  isCollapsed,
  onToggleCollapse,
  onUngroup,
  onTransform,
  onOpenAdjustment,
  onOpenFilter,
  canAdjustLayer,
  onLiquify,
  onEditPath,
  onTrim,
  onMerge,
  canMerge,
  isMultiSelected,
  isActive,
  isSelected,
  onShiftSelect,
  onToggleSelect,
  multiSelectMode,
  dragInsertionEdge,
  canMoveUp,
  canMoveDown,
  hasMask,
  maskKind,
  maskEnabled,
  maskInverted,
  hasSelection,
  editTarget,
  onSetEditTarget,
  onSelect,
  onSelectFromName,
  onToggleVisibility,
  onRename,
  onDelete,
  onMoveLayerUp: onMoveLayerUpRow,
  onMoveLayerDown: onMoveLayerDownRow,
  onAddMask,
  onDeleteMask,
  onToggleMaskEnabled,
  onToggleMaskInverted,
  onAddVectorMask,
  onConvertMaskToVector,
  onConvertMaskToRaster,
  onEditVectorMaskPath,
  onEditAdjustment,
  onSetActiveTool,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
  onDragOver,
}: LayerRowProps) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(layer.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const longPressTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setDraftName(layer.name);
  }, [layer.name]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  // Cancel any pending long-press if the row unmounts mid-press
  // — otherwise we'd dispatch the rename after the row is gone.
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const Icon = TYPE_ICONS[layer.type];

  const commit = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== layer.name) {
      onRename(layer.id, trimmed);
    } else {
      setDraftName(layer.name);
    }
    setEditing(false);
  };

  const startLongPress = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
    }
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTimerRef.current = null;
      setEditing(true);
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Compute drop edge from pointer position within the row. Used
  // both for hover indicator (`onDragOver`) and final drop. Splits
  // at the row's vertical midpoint.
  const edgeFromEvent = (e: React.DragEvent<HTMLLIElement>): 'top' | 'bottom' => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientY - rect.top < rect.height / 2 ? 'top' : 'bottom';
  };

  // Resolve a click into a selection action. Centralizes the
  // modifier rules across the row body, the layer thumb, and the
  // mask thumb so they all behave the same:
  //   - Shift+click       → extend range from the active layer
  //   - Ctrl/Cmd+click    → toggle this layer in additional set
  //   - Multi-mode tap    → toggle this layer in additional set
  //   - Plain click       → make this layer active (always; firing
  //     on an already-active row is intentional so the bg layer
  //     gets re-activated in Fabric and its bounding box appears —
  //     `selectLayerObjects` is the only path that flips
  //     `bg.selectable = true` and runs `setActiveObject`)
  // Returns a discriminant so the caller can decide whether to
  // skip a side effect like `setEditTarget` (the thumbs do that
  // on plain click but not when the user is building selection).
  const resolveSelectAction = (
    e: React.MouseEvent<HTMLElement>,
  ): 'extend' | 'toggle' | 'activate' => {
    if (e.shiftKey) return 'extend';
    if (e.ctrlKey || e.metaKey || multiSelectMode) return 'toggle';
    return 'activate';
  };
  // `source: 'name'` routes the activate path through
  // `onSelectFromName` (row-area click — clears bbox in non-Move
  // tools). `source: 'icon'` (default) goes through `onSelect`, which
  // shows the bbox. Extend / toggle multi-select paths are
  // source-agnostic — they manage the selection set rather than the
  // primary active layer's bbox.
  const dispatchSelectAction = (
    action: 'extend' | 'toggle' | 'activate',
    source: 'icon' | 'name' = 'icon',
  ): void => {
    if (action === 'extend') onShiftSelect(layer.id);
    else if (action === 'toggle') onToggleSelect(layer.id);
    else if (source === 'name') onSelectFromName(layer.id);
    else onSelect(layer.id);
  };

  // Touch drag-reorder via the grip handle. HTML5 drag-and-drop (the
  // desktop row reorder) never fires on touch, so the grip drives the
  // SAME onDragStart/onDragOver/onDrop/onDragEnd callbacks via pointer
  // events — reusing the parent's insertion-line indicator and drop
  // logic. We hit-test the row under the finger with elementFromPoint
  // (the captured pointer keeps events flowing to the handle) and read
  // its `data-layer-index`. `touch-action: none` on the handle stops
  // the list from scrolling once a drag starts.
  const handleDragTargetRef = useRef<{ index: number; edge: 'top' | 'bottom' } | null>(null);
  const handleDraggingRef = useRef(false);
  const onGripPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    handleDraggingRef.current = true;
    handleDragTargetRef.current = { index, edge: 'top' };
    onDragStart(index);
  };
  const onGripPointerMove = (e: React.PointerEvent) => {
    if (!handleDraggingRef.current) return;
    const hit = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest(
      '[data-layer-index]',
    ) as HTMLElement | null;
    if (!hit) return;
    const targetIndex = Number(hit.dataset.layerIndex);
    if (!Number.isFinite(targetIndex)) return;
    const rect = hit.getBoundingClientRect();
    const edge: 'top' | 'bottom' = e.clientY - rect.top < rect.height / 2 ? 'top' : 'bottom';
    handleDragTargetRef.current = { index: targetIndex, edge };
    onDragOver(targetIndex, edge);
  };
  const endGripDrag = (e: React.PointerEvent, drop: boolean) => {
    if (!handleDraggingRef.current) return;
    handleDraggingRef.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }
    const target = handleDragTargetRef.current;
    handleDragTargetRef.current = null;
    // onDrop performs its own drag-state cleanup; onDragEnd covers the
    // cancel path.
    if (drop && target) onDrop(target.index, target.edge);
    else onDragEnd();
  };

  return (
    <li
      draggable
      data-layer-index={index}
      className={`relative flex items-center gap-2 px-2 cursor-pointer border-b border-border/50 transition-colors ${
        isActive || isSelected ? 'bg-accent' : 'hover:bg-accent/50'
      } ${layer.visible ? '' : 'opacity-50'}`}
      style={{ minHeight: 44 }}
      onClick={(e) => dispatchSelectAction(resolveSelectAction(e), 'name')}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver(index, edgeFromEvent(e));
      }}
      onDragEnter={() => onDragEnter(index)}
      onDragEnd={onDragEnd}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(index, edgeFromEvent(e));
      }}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
      onTouchMove={cancelLongPress}
      onTouchCancel={cancelLongPress}
    >
      {/* Insertion line — sits on whichever edge the pointer is
          over, so the user sees exactly where the drop will land. */}
      {dragInsertionEdge && (
        <div
          className="absolute left-0 right-0 h-0.5 bg-primary pointer-events-none"
          style={dragInsertionEdge === 'top' ? { top: -1 } : { bottom: -1 }}
          aria-hidden="true"
        />
      )}

      {/* Touch reorder grip — only on coarse-pointer devices, where
          HTML5 row drag-and-drop doesn't work. Stops touch/click
          propagation so grabbing it doesn't also fire the row's
          tap-to-select or long-press-to-rename. */}
      {IS_COARSE_POINTER && (
        <button
          type="button"
          aria-label="Drag to reorder layer"
          className="shrink-0 -ml-1 flex h-11 items-center px-1 text-muted-foreground cursor-grab touch-none"
          style={{ touchAction: 'none' }}
          onPointerDown={onGripPointerDown}
          onPointerMove={onGripPointerMove}
          onPointerUp={(e) => endGripDrag(e, true)}
          onPointerCancel={(e) => endGripDrag(e, false)}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {/* Tree indentation — 16px per depth level, capped at 4
          levels visually so deep nests don't run the row off the
          panel. The roadmap pins the cap explicitly; tighter
          values make children look less owned by their parent. */}
      {depth > 0 && (
        <div className="shrink-0" style={{ width: 16 * Math.min(depth, 4) }} aria-hidden="true" />
      )}

      {/* Group expand/collapse triangle — only on group rows.
          Reserves 16px of column width on non-group rows so leaf
          rows still align with their group parents. */}
      {isGroup ? (
        <button
          type="button"
          className="w-4 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse(layer.id);
          }}
          title={isCollapsed ? 'Expand group' : 'Collapse group'}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand group' : 'Collapse group'}
        >
          {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      ) : (
        <span className="w-4 shrink-0" aria-hidden="true" />
      )}

      {/* Visibility toggle — 24px touch target. */}
      <button
        type="button"
        className="w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:text-foreground shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility(layer.id);
        }}
        title={layer.visible ? 'Hide layer' : 'Show layer'}
        aria-label={layer.visible ? 'Hide layer' : 'Show layer'}
        aria-pressed={layer.visible}
      >
        {layer.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
      </button>

      {/* Layer thumbnail — 32px square, type icon surrogate for
          now. Click selects this layer (if not already active) AND
          targets the layer's pixel surface for brush/eraser. The
          primary-color border marks the active edit target, so
          when the user is in mask-edit mode the layer thumb
          renders default-bordered while the mask thumb is the one
          highlighted. */}
      <button
        type="button"
        className={`w-8 h-8 flex items-center justify-center rounded border bg-background shrink-0 ${
          isActive && editTarget === 'layer' ? 'border-primary' : 'border-border'
        }`}
        title={
          layer.type === 'adjustment' ? 'Edit adjustment' : hasMask ? 'Edit layer pixels' : 'Layer'
        }
        aria-label={layer.type === 'adjustment' ? 'Edit adjustment' : 'Edit layer pixels'}
        aria-pressed={isActive && editTarget === 'layer'}
        onClick={(e) => {
          e.stopPropagation();
          const action = resolveSelectAction(e);
          dispatchSelectAction(action);
          // Side effects (open Adjustment modal, set edit target,
          // auto-switch tool) only fire on a plain "activate" click.
          // When the user is building a multi-selection (shift/ctrl/
          // multi-mode), tapping the thumb should add the layer to
          // the selection without changing what the brush targets or
          // the active tool.
          if (action !== 'activate') return;
          if (layer.type === 'adjustment') {
            onEditAdjustment(layer.id);
            return;
          }
          onSetEditTarget(layer.id, 'layer');
          // Auto-switch the editor tool to the one whose properties
          // panel matches this layer type — clicking a text layer's
          // icon surfaces the Text properties (font, size, etc.);
          // clicking a shape layer surfaces shape fill/stroke;
          // clicking a vector layer surfaces the Pen tool's panel.
          // Image / paint / empty rows keep the current tool — those
          // layer types don't have a dedicated tool to surface.
          //
          // Re-call `onSelect` after a tool switch so the bounding
          // box shows in the just-switched tool. The original
          // `dispatchSelectAction` above ran while the prior tool was
          // still active; if that tool was in `TOOLS_REQUIRING_LOCKED_LAYERS`
          // (marquee, retouch), `selectLayerObjects` bailed and
          // didn't set Fabric's active object. After the synchronous
          // `canvas.setTool(...)` inside `onSetActiveTool` updates
          // `activeToolRef`, a second `onSelect` lands in the new
          // (non-locked) tool and the transform handles render.
          let didSwitchTool = false;
          if (layer.type === 'text') {
            onSetActiveTool('text');
            didSwitchTool = true;
          } else if (layer.type === 'shape') {
            onSetActiveTool('shape');
            didSwitchTool = true;
          } else if (layer.type === 'vector') {
            onSetActiveTool('pen');
            didSwitchTool = true;
          }
          if (didSwitchTool) onSelect(layer.id);
        }}
      >
        <Icon className="h-4 w-4 text-muted-foreground" />
      </button>

      {/* Mask thumbnail — 24px square, only rendered when this
          layer has a mask attached. Click switches the edit target
          to the mask alpha — brush paints white (reveal), eraser
          clears (hide). Real mask alpha previews replace the
          surrogate icon when the per-layer thumbnail cache lands. */}
      {hasMask && (
        <button
          type="button"
          className={`w-6 h-6 flex items-center justify-center rounded border bg-background shrink-0 ${
            isActive && editTarget === 'mask' ? 'border-primary' : 'border-border'
          }`}
          title={maskKind === 'vector' ? 'Edit vector mask path' : 'Edit mask'}
          aria-label={maskKind === 'vector' ? 'Edit vector mask path' : 'Edit mask'}
          aria-pressed={isActive && editTarget === 'mask'}
          onClick={(e) => {
            e.stopPropagation();
            const action = resolveSelectAction(e);
            dispatchSelectAction(action);
            if (action !== 'activate') return;
            // Vector masks route tap-thumb directly into Path
            // Edit — they don't accept brush strokes, so the
            // edit-target=mask state would just confuse the user
            // (brush would no-op + toast). Raster masks fall
            // through to the existing setEditTarget flow.
            // Tier 2 Group A PR-D.
            if (maskKind === 'vector') {
              onEditVectorMaskPath(layer.id);
              return;
            }
            onSetEditTarget(layer.id, 'mask');
          }}
        >
          {maskKind === 'vector' ? (
            <PenTool className="h-3 w-3 text-muted-foreground" />
          ) : (
            <Square className="h-3 w-3 text-muted-foreground" />
          )}
        </button>
      )}

      {/* Name — flex-grow, truncates on overflow. */}
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-0 text-xs bg-background border border-border rounded px-1.5 py-1"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') {
              setDraftName(layer.name);
              setEditing(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          className="flex-1 min-w-0 text-xs truncate"
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
          title="Double-click (or long-press) to rename"
        >
          {layer.name}
        </span>
      )}

      <LayerOverflowMenu
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        hasMask={hasMask}
        maskKind={maskKind}
        maskEnabled={maskEnabled}
        maskInverted={maskInverted}
        hasSelection={hasSelection}
        isGroup={isGroup}
        onMoveUp={() => onMoveLayerUpRow(layer.id)}
        onMoveDown={() => onMoveLayerDownRow(layer.id)}
        onRename={() => setEditing(true)}
        onDelete={() => onDelete(layer.id)}
        onAddMask={() => onAddMask(layer.id, { fromMarquee: hasSelection })}
        onAddVectorMask={() => onAddVectorMask(layer.id)}
        onDeleteMask={() => onDeleteMask(layer.id)}
        onToggleMaskEnabled={() => onToggleMaskEnabled(layer.id)}
        onToggleMaskInverted={() => onToggleMaskInverted(layer.id)}
        onConvertMaskToVector={() => onConvertMaskToVector(layer.id)}
        onConvertMaskToRaster={() => onConvertMaskToRaster(layer.id)}
        onEditVectorMaskPath={() => onEditVectorMaskPath(layer.id)}
        onUngroup={() => onUngroup(layer.id)}
        onTransform={() => onTransform(layer.id)}
        canAdjust={canAdjustLayer(layer.id)}
        onAdjust={(kind) => onOpenAdjustment(layer.id, kind)}
        onFilter={(kind) => onOpenFilter(layer.id, kind)}
        onLiquify={() => onLiquify(layer.id)}
        isVector={layer.type === 'vector'}
        onEditPath={() => onEditPath(layer.id)}
        onTrim={() => onTrim(layer.id)}
        // Trim is meaningful only for raster-backed layers (image,
        // paint). Text / shape / vector / group / adjustment / empty
        // layers either don't have free transparent borders or have
        // no Fabric image source to trim — hide the entry rather
        // than disable it.
        canTrim={layer.type === 'image' || layer.type === 'paint'}
        onMerge={onMerge}
        canMerge={canMerge}
        isMultiSelected={isMultiSelected}
      />
    </li>
  );
});
